import "./assets/styles/theme.css";
import "./assets/styles/shared-ui.css";
/* Shared-component CSS is hoisted here, in one fixed order, on purpose.
   These four components are used by pages that live in different lazy chunks,
   and each chunk pulled them in whatever order its imports happened to fall —
   which is what mini-css-extract-plugin reported as "Conflicting order" (and
   what made `CI=true npm run build` fail, since CRA treats warnings as errors).
   Importing them from the entry pins one order for every chunk.
   Keep component-level `import "./X.css"` OUT of these four files. */
import "./components/common/RefreshButton.css";
import "./assets/styles/common/SearchableSelect.css";
import "./assets/styles/common/TableToolbar.css";
import "./assets/styles/common/TablePagination.css";
/* Same reason: HrCalendarPg and EmpNotesPage live under a role folder but every
   role wraps and renders them (EmpCalendarPage / CalendarSection / ITACalendarPg
   / PMCalendarPg, and NotesPg / NotesSection / ITANotesPg / PMNotesPg), so their
   CSS lands in all five chunks in a different order each time. */
import "./assets/styles/HR/HrCalendarPg.css";
import "./assets/styles/Employee/EmpNotesPage.css";
/* Same reason: TwoFactorSettings is pulled into the main bundle (via Enforce2FAGate)
   and into each role chunk (via EmpSettingsPage), so its CSS order must be pinned here. */
import "./components/common/TwoFactorSettings.css";
import { useEffect, useRef, useState, Suspense } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import LoginPage from "./pages/Login/LoginPage";
import FilePreviewModal from "./components/common/FilePreviewModal";
import Enforce2FAGate from "./components/common/Enforce2FAGate";
import ConfirmDialogHost from "./components/common/ConfirmDialogHost";
import ResponsiveTables from "./components/common/ResponsiveTables";
import { appConfirm, appPrompt } from "./utils/dialog";
import LoadingState from "./components/common/LoadingState";
import lazyWithRetry from "./utils/lazyWithRetry";

// In-app confirm/prompt available app-wide (no native "localhost says" dialogs).
if (typeof window !== "undefined") {
  window.appConfirm = appConfirm;
  window.appPrompt = appPrompt;
}
//  PERFORMANCE FIX: Lazy load all dashboards
// Before: All 6 dashboards bundled together — every user downloads all roles' code
// After: Only the relevant role's chunk downloads — ~70% JS reduction on first load
//
// lazyWithRetry, not React.lazy: these chunks are fetched after login, which is
// exactly when a tab opened before a deploy asks for bundle names the new build
// no longer ships. See utils/lazyWithRetry.js.
const InternDashboard = lazyWithRetry(() => import("./pages/Intern/InternDashboard"));
const EmployeeDashboard = lazyWithRetry(() => import("./pages/Employee/EmployeeDashboard"));
const HrDashboard = lazyWithRetry(() => import("./pages/HR/HrDashboard"));
const ITAdminDashboard = lazyWithRetry(() => import("./pages/ITAdmin/ITAdminDashboard"));
const PMDashboard = lazyWithRetry(() => import("./pages/PM/PMDashboard"));
const SuperAdminPanel = lazyWithRetry(() => import("./pages/SuperAdmin/SuperAdminPanel"));
const OnboardingFlow = lazyWithRetry(() => import("./pages/Onboarding/OnboardingFlow"));
const PendingVerificationScreen = lazyWithRetry(() => import("./pages/Onboarding/PendingVerification"));
import { canSeeOverview } from "./utils/overviewAccess";

const LandingPage = lazyWithRetry(() => import("./pages/Landing/LandingPage"));
// Lazy for the same reason as the landing page: it pulls in the chart
// primitives, and nobody should download them to reach their own dashboard.
const OverviewPage = lazyWithRetry(() => import("./pages/Overview/OverviewPage"));

function DashboardLoader() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:"var(--font-display)", color:"var(--clr-accent)", flexDirection:"column", gap:"12px" }}>
      <div className="ob-pending-dot" style={{ margin:"0 auto" }} />
      <LoadingState label="Loading your dashboard" />
    </div>
  );
}

/**
 * Every `window.alert(...)` in the app is routed here as an `app:notify` event.
 * That is 215 call sites, so this is the single most-seen piece of feedback in
 * the product — and until now it appeared and vanished with no transition at
 * all, which reads as a glitch rather than a response.
 *
 * AnimatePresence gives it an exit as well as an entrance, and `layout` makes
 * the remaining toasts slide up smoothly when one above them is dismissed
 * instead of jumping. The live region means a screen reader hears it too.
 */
function AppActionPopupHost() {
  const [items, setItems] = useState([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    const handler = (e) => {
      const text = String(e?.detail?.message || "").trim();
      if (!text) return;
      // Errors render red; everything else keeps the default dark toast.
      const isError = e?.detail?.type === "error" || /^(error|failed|cannot|invalid)\b/i.test(text);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => [...prev, { id, text, isError }]);
      setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== id));
      }, 3200);
    };

    window.addEventListener("app:notify", handler);
    return () => window.removeEventListener("app:notify", handler);
  }, []);

  const dismiss = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  const spring = reduce
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 34, mass: 0.8 };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ position: "fixed", right: 18, bottom: 18, zIndex: 9999, display: "grid", gap: 8, maxWidth: 360, pointerEvents: "none" }}
    >
      <AnimatePresence initial={false}>
        {items.map((i) => (
          <motion.div
            key={i.id}
            layout
            initial={reduce ? false : { opacity: 0, x: 28, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: 20, scale: 0.96 }}
            transition={spring}
            onClick={() => dismiss(i.id)}
            style={{
              background: i.isError ? "var(--clr-danger)" : "var(--n-900)",
              color: i.isError ? "var(--n-0)" : "var(--clr-surface)",
              borderRadius: "var(--radius-md)",
              padding: "11px 14px",
              boxShadow: "var(--shadow-lg)",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              pointerEvents: "auto",
            }}
          >
            {i.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/**
 * What a signed-out visitor sees.
 *
 * `/` is the landing page, always. `/login` is the form. Everyone arriving at
 * the bare URL reads what the product is before they are asked for a password;
 * anyone who already knows can go straight to `/login` and skip it.
 *
 * PATH, NOT HASH. This used to route on `#login`, which quietly broke five
 * things: api.js sends `window.location.href = '/login'` on logout, on
 * logoutAll and on any 401, SocketContext does the same when the socket is
 * rejected, and PendingVerification does it too. All five landed on `/login`
 * with no hash — so PublicView saw no hash and showed the LANDING page.
 * Signing out dropped you on the marketing page instead of the login form.
 * Routing on the path is what those five call sites always expected.
 *
 * Still no react-router: App.jsx is the router, switching on user.role, and
 * two public screens do not justify the dependency.
 *
 * PRODUCTION NOTE: `/login` is a client route with no file behind it, so
 * whatever serves the build must fall back to index.html for unknown paths.
 * The CRA dev server already does. The Express backend does not serve the
 * build at all today, so this is a deploy-config requirement, not a code one.
 */
const LOGIN_PATH = "/login";
const isLoginPath = (p) => /^\/login\/?$/i.test(p || "");
const isOverviewPath = (p) => /^\/overview\/?$/i.test(p || "");

function PublicView() {
  const [path, setPath] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname);

  useEffect(() => {
    // Back/forward. pushState does not fire popstate, so navigate() below sets
    // the state itself — this listener is only for the browser's own buttons.
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (to) => {
    window.history.pushState({}, "", to);
    setPath(to);
    window.scrollTo(0, 0);
  };

  if (isLoginPath(path)) {
    // Back from the form returns to the landing page rather than out of the
    // app, because pushState put a real entry in the history stack.
    return <LoginPage onBack={() => navigate("/")} />;
  }

  return (
    <Suspense fallback={<DashboardLoader />}>
      <LandingPage onSignIn={() => navigate(LOGIN_PATH)} />
    </Suspense>
  );
}

function AppRoutes() {
  const { user, logout, setUser, refreshUser, loading } = useAuth();
  const authCheckedRef = useRef(false);
  // `/overview` is the one signed-in screen that is not a dashboard, so it is
  // tracked here rather than inside any single role's SPA.
  const [path, setPath] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    // Dashboards live inside their own tab state and never touch the URL, so
    // this event is how they ask for the overview without importing it.
    const onGo = (e) => {
      const to = e?.detail?.to || "/";
      window.history.pushState({}, "", to);
      setPath(to);
      window.scrollTo(0, 0);
    };
    window.addEventListener("app:navigate", onGo);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("app:navigate", onGo);
    };
  }, []);

  useEffect(() => {
    const nativeAlert = window.alert?.bind(window);
    window.alert = (message) => {
      const text = String(message || "").trim();
      if (!text) return;
      window.dispatchEvent(new CustomEvent("app:notify", { detail: { message: text } }));
    };
    return () => {
      if (nativeAlert) window.alert = nativeAlert;
    };
  }, []);

  useEffect(() => {
    if (!loading && !user && !authCheckedRef.current) {
      authCheckedRef.current = true;
    }
  }, [loading, user]);

  // Once signed in, take `/login` out of the URL. replaceState, not pushState:
  // the login form is not somewhere Back should return you to now that the
  // session exists — it would render the dashboard anyway and read as broken.
  useEffect(() => {
    if (user && isLoginPath(window.location.pathname)) {
      window.history.replaceState({}, "", "/");
    }
  }, [user]);

  if (loading) {
    return <DashboardLoader />;
  }

  if (!user) return <PublicView />;

  const st = user.status || "";

  // Before the role switch, and before the onboarding gates below it: the
  // overview belongs to no single role's dashboard. Not offered to people
  // still in onboarding — they have a form to finish, not a company to survey.
  //
  // `canSeeOverview` matches the guard on /api/overview. Hiding the topbar
  // icon is not a guard — /overview is a typeable URL and survives a refresh —
  // so the route checks too. The server is still the one that decides; this
  // just means an employee who types the URL gets their own dashboard rather
  // than a page that loads and then fills with a 403.
  if (isOverviewPath(path) && canSeeOverview(user)
      && st !== "TEMP" && st !== "ONBOARDING_SUBMITTED") {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <OverviewPage
          onBack={() => {
            window.history.pushState({}, "", "/");
            setPath("/");
          }}
        />
      </Suspense>
    );
  }

  if (st === "TEMP") {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <OnboardingFlow
          user={user}
          onSubmit={async () => {
            setUser((prev) => ({ ...prev, status: "ONBOARDING_SUBMITTED" }));
            try {
              if (refreshUser) await refreshUser();
            } catch (_) {}
          }}
          onLogout={logout}
        />
      </Suspense>
    );
  }

  if (st === "ONBOARDING_SUBMITTED") {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <PendingVerificationScreen user={user} onLogout={logout} />
      </Suspense>
    );
  }

  // 2FA enforcement: if the org requires 2FA and this (non-superadmin, non-Google)
  // user hasn't set it up, block the dashboard behind a mandatory setup gate.
  // Backend sends `two_factor_setup_required` (already Google/onboarding-aware).
  if (user.role !== "superadmin" && user.two_factor_setup_required === true) {
    return (
      <Enforce2FAGate
        user={user}
        onLogout={logout}
        onEnabled={() =>
          setUser((prev) => ({ ...prev, two_factor_enabled: 1, two_factor_setup_required: false }))
        }
      />
    );
  }

  switch (user.role) {
    case "employee":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <EmployeeDashboard onLogout={logout} user={user} />
        </Suspense>
      );
    case "hr":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <HrDashboard onLogout={logout} />
        </Suspense>
      );
    case "intern":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <InternDashboard onLogout={logout} user={user} />
        </Suspense>
      );
    case "itadmin":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <ITAdminDashboard onLogout={logout} />
        </Suspense>
      );
    case "pm":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <PMDashboard onLogout={logout} />
        </Suspense>
      );
    case "superadmin":
      return (
        <Suspense fallback={<DashboardLoader />}>
          <SuperAdminPanel onLogout={logout} />
        </Suspense>
      );
    default:
      return <LoginPage />;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <AppRoutes />
        <AppActionPopupHost />
        <ResponsiveTables />
        <ConfirmDialogHost />
        <FilePreviewModal />
      </SocketProvider>
    </AuthProvider>
  );
}
